import React, { useEffect, useState } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import dayjs from "dayjs";

//MUI
import { AppBar, Tabs, Tab } from "@material-ui/core";

// Actions
import {
    getApprovalRequests,
    cancelApprovalRequest,
    decideApprovalRequest,
} from "../store/actionsForApproval/actions";

// Components
import TabPanel from "../component/TabPanel";

// Utils
import { warningv2, alert, permissionError } from "../util/Alert";
import { useAccess } from "../hooks/useAccess";

const a11yProps = (index) => {
    return {
        id: `approval-tab-${index}`,
        "aria-controls": `approval-tabpanel-${index}`,
    };
};

// Helper function to format action type
const formatActionType = (actionType) => {
    const actionTypeMap = {
        SEND_REWARD: "Send Reward",
        MAKE_VIP: "Make VIP",
        DIAS_APP_PURCHASE: "Diamond App Purchase",
        REOPEN_TREASURE_BOX: "Reopen Treasure Box",
    };
    return actionTypeMap[actionType] || actionType;
};

// Helper function to get status badge class
const getStatusBadgeClass = (status) => {
    switch (status) {
        case "PENDING":
            return "badge badge-warning";
        case "APPROVED":
            return "badge badge-success";
        case "REJECTED":
            return "badge badge-danger";
        default:
            return "badge badge-light";
    }
};

const ActionsForApproval = (props) => {
    const dispatch = useDispatch();
    const { isSuperAdmin, getRole, getCurrentAdminId } = useAccess();
    const adminRole = getRole();
    const currentAdminId = getCurrentAdminId();

    // Local state
    const [tabValue, setTabValue] = useState(0);
    const [searchValue, setSearchValue] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Redux state
    const {
        pendingRequests,
        approvedRequests,
        rejectedRequests,
        pendingMeta,
        approvedMeta,
        rejectedMeta,
        loading,
    } = useSelector((state) => state.actionsForApproval);

    // Get current data based on active tab
    const getCurrentData = () => {
        switch (tabValue) {
            case 0: // PENDING
                return { data: pendingRequests, meta: pendingMeta };
            case 1: // APPROVED
                return { data: approvedRequests, meta: approvedMeta };
            case 2: // REJECTED
                return { data: rejectedRequests, meta: rejectedMeta };
            default:
                return { data: [], meta: { total: 0 } };
        }
    };

    const { data: currentData, meta: currentMeta } = getCurrentData();

    // Filter data based on search and role-based access
    const getFilteredData = () => {
        let filteredData = currentData;

        // Role-based filtering (additional security layer)
        // Non-SuperAdmins should only see their own requests
        if (adminRole !== "SuperAdmin" && currentAdminId) {
            filteredData = filteredData.filter(
                (request) => request.requestedBy?._id === currentAdminId
            );
        }

        // Search filtering
        if (searchValue.trim()) {
            const searchTerm = searchValue.trim().toUpperCase();
            filteredData = filteredData.filter((request) => {
                return (
                    formatActionType(request.actionType)
                        .toUpperCase()
                        .includes(searchTerm) ||
                    request.requestedBy?.name?.toUpperCase().includes(searchTerm) ||
                    request.requestedBy?.email?.toUpperCase().includes(searchTerm) ||
                    request.reviewedBy?.name?.toUpperCase().includes(searchTerm)
                );
            });
        }

        return filteredData;
    };

    const filteredData = getFilteredData();

    // Load data on component mount
    useEffect(() => {
        loadDataForTab("PENDING");
    }, []);

    // Load data for specific tab
    const loadDataForTab = (tabStatus) => {
        props.getApprovalRequests({
            status: tabStatus,
            page: 1,
            limit: itemsPerPage,
        });
    };

    // Handle tab change
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        setCurrentPage(1);
        setSearchValue("");

        // Load data if not already loaded
        const statusMap = ["PENDING", "APPROVED", "REJECTED"];
        const tabStatus = statusMap[newValue];

        const hasData = getCurrentData().data.length > 0;
        if (!hasData) {
            loadDataForTab(tabStatus);
        }
    };

    // Handle search
    const handleSearch = (e) => {
        const value = e.target.value;
        setSearchValue(value);
        setCurrentPage(1);
    };

    // Action handlers
    const handleCancel = (request) => {
        const data = warningv2(
            "CANCEL REQUEST",
            "Are you sure you want to cancel this approval request?"
        );
        data
            .then((confirmed) => {
                if (confirmed) {
                    props
                        .cancelApprovalRequest(request._id)
                        .then(() => {
                            alert("Success!", "Request cancelled successfully!", "success");
                            const statusMap = [
                                "PENDING",
                                "APPROVED",
                                "REJECTED"
                            ];
                            loadDataForTab(statusMap[tabValue]);
                        })
                        .catch((error) => console.error("Cancel request error:", error));
                }
            })
            .catch(console.error);
    };

    const handleApprove = (request) => {
        const data = warningv2(
            "APPROVE REQUEST",
            "Are you sure you want to approve this request?"
        );
        data
            .then((confirmed) => {
                if (confirmed) {
                    if (!isSuperAdmin) return permissionError();

                    props
                        .decideApprovalRequest(request._id, "APPROVE")
                        .then(() => {
                            alert("Success!", "Request approved successfully!", "success");
                            const statusMap = [
                                "PENDING",
                                "APPROVED",
                                "REJECTED"
                            ];
                            loadDataForTab(statusMap[tabValue]);
                        })
                        .catch((error) => console.error("Approve request error:", error));
                }
            })
            .catch(console.error);
    };

    const handleReject = (request) => {
        const data = warningv2(
            "REJECT REQUEST",
            "Are you sure you want to reject this request?"
        );
        data
            .then((confirmed) => {
                if (confirmed) {
                    if (!isSuperAdmin) return permissionError();

                    props
                        .decideApprovalRequest(request._id, "REJECT")
                        .then(() => {
                            alert("Success!", "Request rejected successfully!", "success");
                            const statusMap = [
                                "PENDING",
                                "APPROVED",
                                "REJECTED"
                            ];
                            loadDataForTab(statusMap[tabValue]);
                        })
                        .catch((error) => console.error("Reject request error:", error));
                }
            })
            .catch(console.error);
    };

    // Pagination
    const totalItems = filteredData.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentItems = filteredData.slice(startIndex, endIndex);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(parseInt(e.target.value));
        setCurrentPage(1);
    };

    const getRewardDetails = (payload) => {
        if (payload?.data?.type === "frame") {
            return {
                name: payload.snapshot?.name,
                image: payload.snapshot?.imagePreview || payload.snapshot?.image,
            };
        } else if (payload?.data?.type === "svga") {
            return {
                name: payload.snapshot?.name,
                image: payload.snapshot?.imagePreview || payload.snapshot?.image,
            };
        } else if (payload?.data?.type === "background") {
            return {
                name: payload.snapshot?.name,
                image: payload.snapshot?.imagePreview || payload.snapshot?.image,
            };
        } else if (payload?.data?.type === "gift") {
            return {
                name: payload.snapshot?.name,
                image: payload.snapshot?.imagePreview || payload.snapshot?.image,
            };
        } else if (payload?.data?.type === "diamond") {
            return {
                name: `Diamonds: ${parseInt(payload.data.diamond).toLocaleString()}`,
                image: null,
            };
        } else {
            return { name: "N/A", image: null };
        }
    };

    return (
        <>
            <div className="page-title">
                <div className="row">
                    <div className="col-12 col-md-6 order-md-1 order-last">
                        <h3 className="mb-3 text-light" style={{ color: "#e4eeff" }}>
                            Actions For Approval
                        </h3>
                    </div>
                    <div className="col-12 col-md-6 order-md-2 order-first">
                        <nav
                            aria-label="breadcrumb"
                            className="breadcrumb-header float-start float-lg-end"
                        >
                            <ol className="breadcrumb">
                                <li className="breadcrumb-item">
                                    <Link to="/admin/dashboard" className="text-danger">
                                        Dashboard
                                    </Link>
                                </li>
                                <li className="breadcrumb-item active" aria-current="page">
                                    Actions For Approval
                                </li>
                            </ol>
                        </nav>
                    </div>
                </div>
            </div>

            <AppBar position="static">
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    aria-label="Approval Requests Tabs"
                    indicatorColor="primary"
                >
                    <Tab
                        label={`Pending (${pendingMeta.total || 0})`}
                        {...a11yProps(0)}
                    />
                    <Tab
                        label={`Approved (${approvedMeta.total || 0})`}
                        {...a11yProps(1)}
                    />
                    <Tab
                        label={`Rejected (${rejectedMeta.total || 0})`}
                        {...a11yProps(2)}
                    />
                </Tabs>
            </AppBar>

            <div className="row">
                <div className="col">
                    <div className="card" id="card">
                        <div className="card-header pb-0">
                            {/* Search */}
                            <div className="row my-3">
                                <div className="col-xs-12 col-sm-12 col-md-6 col-lg-8 float-left">
                                    <div className="text-left align-sm-left d-md-flex d-lg-flex justify-content-start">
                                        <h5 className="text-light">
                                            {
                                                ["Pending", "Approved", "Rejected"][
                                                tabValue
                                                ]
                                            }{" "}
                                            Requests
                                        </h5>
                                    </div>
                                </div>
                                <div className="col-xs-12 col-sm-12 col-md-6 col-lg-4 float-right">
                                    <form action="">
                                        <div className="input-group mb-3 border rounded-pill">
                                            <div className="input-group-prepend border-0">
                                                <div id="button-addon4" className="btn text-danger">
                                                    <i className="fas fa-search mt-2"></i>
                                                </div>
                                            </div>
                                            <input
                                                type="search"
                                                id="searchBar"
                                                autoComplete="off"
                                                placeholder="Search requests..."
                                                aria-describedby="button-addon4"
                                                className="form-control bg-none border-0 rounded-pill searchBar"
                                                value={searchValue}
                                                onChange={handleSearch}
                                            />
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <TabPanel value={tabValue} index={0}>
                {/* Pending Requests Content */}
                <div className="row">
                    <div className="col">
                        <div className="card" id="card">
                            <div className="card-body card-overflow pt-0">
                                <table className="table table-striped mt-5 text-center">
                                    <thead>
                                        <tr>
                                            <th>No.</th>
                                            <th>Action Type</th>
                                            <th>Details</th>
                                            <th>Requested By</th>
                                            <th>Request Date</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredData?.length > 0 ? (
                                            filteredData?.map((request, index) => {
                                                return (
                                                    <tr key={request._id}>
                                                        <td>{startIndex + index + 1}</td>
                                                        <td>{formatActionType(request.actionType)}</td>
                                                        <td>
                                                            <div>
                                                                {request.actionType === "REOPEN_TREASURE_BOX" && (
                                                                    <div className="d-flex flex-column align-items-center">
                                                                        <span>{`Username: ${request.payload?.snapshot?.name}`}</span>
                                                                        <span>{`Party Room: ${request.payload?.snapshot?.audioRoomName}`}</span>
                                                                        <span>{`Video Live: ${request.payload?.snapshot?.videoRoomName}`}</span>
                                                                        <span>{`User ID: ${request.payload.data.uniqueId}`}</span>
                                                                    </div>
                                                                )}
                                                                {request.actionType === "MAKE_VIP" && (
                                                                    <div className="d-flex flex-column align-items-center">
                                                                        <span>{`${request.payload?.data?.validity} ${request.payload?.data?.validityType}`}</span>
                                                                        <span>{`User ID: ${request.payload.data.uniqueId}`}</span>
                                                                    </div>
                                                                )}
                                                                {request.actionType === "SEND_REWARD" &&
                                                                    (() => {
                                                                        const rewardDetails = getRewardDetails(
                                                                            request.payload
                                                                        );
                                                                        return (
                                                                            <div className="d-flex flex-column align-items-center">
                                                                                <div className="d-flex align-items-center">
                                                                                    {rewardDetails.image ? (
                                                                                        <img
                                                                                            src={
                                                                                                "https://devadmin.vibelynk.com/" +
                                                                                                rewardDetails.image
                                                                                            }
                                                                                            alt={rewardDetails.name}
                                                                                            style={{
                                                                                                width: "30px",
                                                                                                height: "30px",
                                                                                                objectFit: "cover",
                                                                                                marginRight: "10px",
                                                                                            }}
                                                                                        />
                                                                                    ) : null}
                                                                                    <span>{rewardDetails.name}</span>
                                                                                </div>
                                                                                {request.payload?.data?.type !==
                                                                                    "diamond" &&
                                                                                    request.payload?.data?.type !==
                                                                                    "gift" && (
                                                                                        <span>{`${request.payload.data.validity} ${request.payload.data.validityType}`}</span>
                                                                                    )}

                                                                                {request.payload?.data?.type === 'gift' && (
                                                                                    <span>{`x ${request.payload?.data?.quantity}`}</span>
                                                                                )}
                                                                                <span>{`User ID: ${request.payload.data.uniqueId}`}</span>
                                                                            </div>
                                                                        );
                                                                    })()}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div>
                                                                <strong>
                                                                    {request.requestedBy?.name || "N/A"}
                                                                </strong>
                                                                <br />
                                                                <small className="text-muted">
                                                                    {request.requestedBy?.email}
                                                                </small>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            {dayjs(request.createdAt).format(
                                                                "MMM DD, YYYY HH:mm"
                                                            )}
                                                        </td>
                                                        <td>
                                                            <span
                                                                className={getStatusBadgeClass(request.status)}
                                                            >
                                                                {request.status.toLowerCase()}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div>
                                                                {request.requestedBy?._id ===
                                                                    currentAdminId && (
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm danger text-white mr-2"
                                                                            onClick={() => handleCancel(request)}
                                                                            disabled={request.status !== "PENDING"}
                                                                        >
                                                                            Cancel
                                                                        </button>
                                                                    )}
                                                                {/* Approve/Reject buttons - Only SuperAdmins can approve/reject */}
                                                                {adminRole === "SuperAdmin" && (
                                                                    <>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-success mr-2"
                                                                            onClick={() => handleApprove(request)}
                                                                            disabled={request.status !== "PENDING"}
                                                                        >
                                                                            Approve
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-danger"
                                                                            onClick={() => handleReject(request)}
                                                                            disabled={request.status !== "PENDING"}
                                                                        >
                                                                            Reject
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="6" align="center">
                                                    Nothing to show!!
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
                {/* Approved Requests Content */}
                <div className="row">
                    <div className="col">
                        <div className="card" id="card">
                            <div className="card-body card-overflow pt-0">
                                <table className="table table-striped mt-5 text-center">
                                    <thead>
                                        <tr>
                                            <th>No.</th>
                                            <th>Action Type</th>
                                            <th>Details</th>
                                            <th>Requested By</th>
                                            <th>Request Date</th>
                                            <th>Status</th>
                                            <th>Reviewed By</th>
                                            <th>Review Date</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredData?.length > 0 ? (
                                            filteredData?.map((request, index) => {
                                                return (
                                                    <tr key={request._id}>
                                                        <td>{startIndex + index + 1}</td>
                                                        <td>{formatActionType(request.actionType)}</td>
                                                        <td>
                                                            <div>
                                                                {request.actionType === "REOPEN_TREASURE_BOX" && (
                                                                    <div className="d-flex flex-column align-items-center">
                                                                        <span>{`Username: ${request.payload?.snapshot?.name}`}</span>
                                                                        <span>{`Party Room: ${request.payload?.snapshot?.audioRoomName}`}</span>
                                                                        <span>{`Video Live: ${request.payload?.snapshot?.videoRoomName}`}</span>
                                                                        <span>{`User ID: ${request.payload.data.uniqueId}`}</span>
                                                                    </div>
                                                                )}
                                                                {request.actionType === "MAKE_VIP" && (
                                                                    <div className="d-flex flex-column align-items-center">
                                                                        <span>{`${request.payload?.data?.validity} ${request.payload?.data?.validityType}`}</span>
                                                                        <span>{`User ID: ${request.payload.data.uniqueId}`}</span>
                                                                    </div>
                                                                )}
                                                                {request.actionType === "SEND_REWARD" &&
                                                                    (() => {
                                                                        const rewardDetails = getRewardDetails(
                                                                            request.payload
                                                                        );
                                                                        return (
                                                                            <div className="d-flex flex-column align-items-center">
                                                                                <div className="d-flex align-items-center">
                                                                                    {rewardDetails.image ? (
                                                                                        <img
                                                                                            src={
                                                                                                "https://devadmin.vibelynk.com/" +
                                                                                                rewardDetails.image
                                                                                            }
                                                                                            alt={rewardDetails.name}
                                                                                            style={{
                                                                                                width: "30px",
                                                                                                height: "30px",
                                                                                                objectFit: "cover",
                                                                                                marginRight: "10px",
                                                                                            }}
                                                                                        />
                                                                                    ) : null}
                                                                                    <span>{rewardDetails.name}</span>
                                                                                </div>
                                                                                {request.payload?.data?.type !==
                                                                                    "diamond" &&
                                                                                    request.payload?.data?.type !==
                                                                                    "gift" && (
                                                                                        <span>{`${request.payload.data.validity} ${request.payload.data.validityType}`}</span>
                                                                                    )}

                                                                                {request.payload?.data?.type === 'gift' && (
                                                                                    <span>{`x ${request.payload?.data?.quantity}`}</span>
                                                                                )}
                                                                                <span>{`User ID: ${request.payload.data.uniqueId}`}</span>
                                                                            </div>
                                                                        );
                                                                    })()}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div>
                                                                <strong>
                                                                    {request.requestedBy?.name || "N/A"}
                                                                </strong>
                                                                <br />
                                                                <small className="text-muted">
                                                                    {request.requestedBy?.email}
                                                                </small>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            {dayjs(request.createdAt).format(
                                                                "MMM DD, YYYY HH:mm"
                                                            )}
                                                        </td>
                                                        <td>
                                                            <span
                                                                className={getStatusBadgeClass(request.status)}
                                                            >
                                                                {request.status.toLowerCase()}
                                                            </span>
                                                        </td>
                                                        <td>{request.reviewedBy?.name || "N/A"}</td>
                                                        <td>
                                                            {request.reviewedAt
                                                                ? dayjs(request.reviewedAt).format(
                                                                    "MMM DD, YYYY HH:mm"
                                                                )
                                                                : "N/A"}
                                                        </td>
                                                        <td>
                                                            <span className="text-muted">-</span>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="8" align="center">
                                                    Nothing to show!!
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
                {/* Rejected Requests Content */}
                <div className="row">
                    <div className="col">
                        <div className="card" id="card">
                            <div className="card-body card-overflow pt-0">
                                <table className="table table-striped mt-5 text-center">
                                    <thead>
                                        <tr>
                                            <th>No.</th>
                                            <th>Action Type</th>
                                            <th>Details</th>
                                            <th>Requested By</th>
                                            <th>Request Date</th>
                                            <th>Status</th>
                                            <th>Reviewed By</th>
                                            <th>Review Date</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredData?.length > 0 ? (
                                            filteredData?.map((request, index) => {
                                                return (
                                                    <tr key={request._id}>
                                                        <td>{startIndex + index + 1}</td>
                                                        <td>{formatActionType(request.actionType)}</td>
                                                        <td>
                                                            <div>
                                                                {request.actionType === "REOPEN_TREASURE_BOX" && (
                                                                    <div className="d-flex flex-column align-items-center">
                                                                        <span>{`Username: ${request.payload?.snapshot?.name}`}</span>
                                                                        <span>{`Party Room: ${request.payload?.snapshot?.audioRoomName}`}</span>
                                                                        <span>{`Video Live: ${request.payload?.snapshot?.videoRoomName}`}</span>
                                                                        <span>{`User ID: ${request.payload.data.uniqueId}`}</span>
                                                                    </div>
                                                                )}
                                                                {request.actionType === "MAKE_VIP" && (
                                                                    <div className="d-flex flex-column align-items-center">
                                                                        <span>{`${request.payload?.data?.validity} ${request.payload?.data?.validityType}`}</span>
                                                                        <span>{`User ID: ${request.payload.data.uniqueId}`}</span>
                                                                    </div>
                                                                )}
                                                                {request.actionType === "SEND_REWARD" &&
                                                                    (() => {
                                                                        const rewardDetails = getRewardDetails(
                                                                            request.payload
                                                                        );
                                                                        return (
                                                                            <div className="d-flex flex-column align-items-center">
                                                                                <div className="d-flex align-items-center">
                                                                                    {rewardDetails.image ? (
                                                                                        <img
                                                                                            src={
                                                                                                "https://devadmin.vibelynk.com/" +
                                                                                                rewardDetails.image
                                                                                            }
                                                                                            alt={rewardDetails.name}
                                                                                            style={{
                                                                                                width: "30px",
                                                                                                height: "30px",
                                                                                                objectFit: "cover",
                                                                                                marginRight: "10px",
                                                                                            }}
                                                                                        />
                                                                                    ) : null}
                                                                                    <span>{rewardDetails.name}</span>
                                                                                </div>
                                                                                {request.payload?.data?.type !==
                                                                                    "diamond" &&
                                                                                    request.payload?.data?.type !==
                                                                                    "gift" && (
                                                                                        <span>{`${request.payload.data.validity} ${request.payload.data.validityType}`}</span>
                                                                                    )}

                                                                                {request.payload?.data?.type === 'gift' && (
                                                                                    <span>{`x ${request.payload?.data?.quantity}`}</span>
                                                                                )}
                                                                                <span>{`User ID: ${request.payload.data.uniqueId}`}</span>
                                                                            </div>
                                                                        );
                                                                    })()}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div>
                                                                <strong>
                                                                    {request.requestedBy?.name || "N/A"}
                                                                </strong>
                                                                <br />
                                                                <small className="text-muted">
                                                                    {request.requestedBy?.email}
                                                                </small>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            {dayjs(request.createdAt).format(
                                                                "MMM DD, YYYY HH:mm"
                                                            )}
                                                        </td>
                                                        <td>
                                                            <span
                                                                className={getStatusBadgeClass(request.status)}
                                                            >
                                                                {request.status.toLowerCase()}
                                                            </span>
                                                        </td>
                                                        <td>{request.reviewedBy?.name || "N/A"}</td>
                                                        <td>
                                                            {request.reviewedAt
                                                                ? dayjs(request.reviewedAt).format(
                                                                    "MMM DD, YYYY HH:mm"
                                                                )
                                                                : "N/A"}
                                                        </td>
                                                        <td>
                                                            <span className="text-muted">-</span>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="8" align="center">
                                                    Nothing to show!!
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </TabPanel>
        </>
    );
};

export default connect(null, {
    getApprovalRequests,
    cancelApprovalRequest,
    decideApprovalRequest,
})(ActionsForApproval);
